import {
  Box,
  Button,
  CloseButton,
  HStack,
  Heading,
  Switch,
  Text,
  VStack
} from "@chakra-ui/react"
import { keyframes } from "@emotion/react"
import useTranslate from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { type ReactNode, useState } from "react"
import { useHistory } from "react-router-dom"
import {
  FeatureAction,
  FeatureKey,
  FeaturePermission
} from "@pathwright/graphql-client/src/generated"
import useUpdateCFP from "../../feature/useUpdateCFP"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { PopoverTip } from "../../tips/PopoverTip"
import useTipStatus from "../../tips/useTipStatus"
import useUnifiedHomePermissions from "./useUnifiedHomePermissions"

const _tPrefix = "new_home_screen.preview"

function useToggleHomeScreenView() {
  const pwContext = usePathwrightContext()
  const history = useHistory()

  const { canView, userScopedCFP, refetchPermissions } =
    useUnifiedHomePermissions()

  const [mutation] = useUpdateCFP(userScopedCFP?.id)

  // Toggle the view permission for the unified home screen.
  // Accepts an optional permission to set, otherwise toggles based on current state.
  async function toggleViewPermission(permission?: FeaturePermission) {
    const toggledPermission = canView
      ? FeaturePermission.Disabled
      : FeaturePermission.Everyone

    const newPermission = permission ?? toggledPermission

    const promise = mutation({
      featureKey: FeatureKey.HomeScreenUnified,
      featureAction: FeatureAction.HomeScreenView,
      context: { user_id: pwContext.me?.id },
      permissionInContext: newPermission
    })

    if (!userScopedCFP) {
      // Await the result of the create mutation before refetching permissions.
      await promise
      // Refetch permissions to ensure we have the latest CFPs.
      await refetchPermissions()
    }

    // Redirect to appropriate home screen view based on new permission.
    const pathname =
      newPermission === FeaturePermission.Everyone ? "/home/" : "/home/learn/"

    history.push(pathname)
  }

  return toggleViewPermission
}

export function HomeScreenViewPrompt() {
  const { t } = useTranslate()
  const tPrefix = `${_tPrefix}.prompt`

  return (
    <HStack>
      <Pathicon icon="sparkles" />
      <Text as="span">{t(`${tPrefix}.Try the new Home Screen`)}</Text>
    </HStack>
  )
}

export function HomeScreenViewToggle() {
  const { canView, canPreview } = useUnifiedHomePermissions()
  const toggleHomeScreenView = useToggleHomeScreenView()

  return canPreview ? (
    <HStack w="100%">
      <Switch.Root
        id="toggle-home-screen-view"
        checked={canView === true}
        onChange={() => toggleHomeScreenView()}
        colorPalette="brand"
        flexGrow={1}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Switch.HiddenInput />
        <Switch.Label>
          <HomeScreenViewPrompt />
        </Switch.Label>
        <Switch.Control />
      </Switch.Root>
    </HStack>
  ) : null
}

const tipId = "home-screen-view-initial-prompt" as const

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.6); opacity: 0.45; }
  100% { transform: scale(1); opacity: 1; }
`

function HomeScreenPreviewIndicatorDot() {
  return (
    <Box
      pos="absolute"
      top="-2px"
      right="-2px"
      w="10px"
      h="10px"
      borderRadius="full"
      bg="brand.500"
      boxShadow="0 0 0 2px white"
      animation={`${pulse} 2s ease-in-out infinite`}
      css={{ "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}
      pointerEvents="none"
      zIndex={1}
    />
  )
}

function HomeScreenPreviewPopoverBody({
  onPreview,
  onDismiss,
  dismissable,
  loading
}: {
  onPreview: () => void
  onDismiss?: () => void
  dismissable?: boolean
  loading?: boolean
}) {
  const { t } = useTranslate()
  const tPrefix = `${_tPrefix}.popover`

  return (
    <Box pos="relative">
      {dismissable && (
        <CloseButton
          size="sm"
          pos="absolute"
          top={2}
          right={2}
          opacity={0.65}
          _hover={{ opacity: 1 }}
          onClick={onDismiss}
          aria-label={t(`${tPrefix}.dismiss.Dismiss tip`)}
        />
      )}
      <VStack align="start" gap={3} p={4} pr={dismissable ? 10 : 4}>
        <HStack gap={2} align="center">
          <Pathicon icon="sparkles" size="1.2em" />
          <Heading as="h4" fontSize="md" fontWeight="bold" m={0}>
            {t(`${tPrefix}.title.Try the new Home Screen`)}
          </Heading>
        </HStack>
        <Text fontSize="sm" m={0}>
          {t(
            `${tPrefix}.body.The new Home Screen keeps all your Paths on one focused screen and gives you more control on which to focus on.`
          )}
        </Text>
        <Button
          colorPalette="brand"
          size="sm"
          w="100%"
          onClick={onPreview}
          loading={loading}
          loadingText={t(`${tPrefix}.action.Preview Home Screen`)}
        >
          {t(`${tPrefix}.action.Preview Home Screen`)}
        </Button>
        <Text fontSize="xs" fontStyle="italic" opacity={0.7} m={0}>
          {t(
            `${tPrefix}.footer.(You can toggle the preview off/on anytime in your profile settings.)`
          )}
        </Text>
      </VStack>
    </Box>
  )
}

export function HomeScreenViewPopoverTip({
  children
}: {
  children: ReactNode
}) {
  const [loading, setLoading] = useState(false)
  const [tipStatus, setTipStatus, tipLoading] = useTipStatus(tipId)
  const { canPreview, canView, userScopedCFP } = useUnifiedHomePermissions()
  const toggleHomeScreenView = useToggleHomeScreenView()

  const isAutoOpen =
    !tipLoading &&
    canPreview === true &&
    userScopedCFP === null &&
    tipStatus !== "dismissed"

  const showIndicator =
    !tipLoading &&
    canPreview === true &&
    canView === false &&
    !isAutoOpen &&
    (userScopedCFP !== null || tipStatus === "dismissed")

  async function handlePreview() {
    setLoading(true)
    try {
      await toggleHomeScreenView(FeaturePermission.Everyone)
    } finally {
      setLoading(false)
    }
  }

  function handleDismiss() {
    setTipStatus("dismissed")
  }

  if (!isAutoOpen && !showIndicator) {
    return <>{children}</>
  }

  return (
    <PopoverTip
      interaction={showIndicator ? "hover" : "always-open"}
      showIndicator={showIndicator}
      renderIndicator={() => <HomeScreenPreviewIndicatorDot />}
      displayArrow={showIndicator ? undefined : true}
      renderBody={() => (
        <HomeScreenPreviewPopoverBody
          onPreview={handlePreview}
          onDismiss={showIndicator ? undefined : handleDismiss}
          dismissable={!showIndicator}
          loading={loading}
        />
      )}
    >
      {children}
    </PopoverTip>
  )
}
